/**
 * § 7.15 Phase 1 — Gate-Anchored EVPI Clarifier renderer.
 *
 * Renders a structured clarifier payload emitted by the backend
 * when Invention G's pre-flight gate refused a tool call. The
 * patentable mechanism is the typed-schema bridge from G's
 * refusal vocabulary to a multiple-choice question surface; this
 * component is the user-facing half of that bridge.
 *
 * Each question is rendered as a button group (multiple_choice)
 * or a free-text input (free_text). Selecting a value composes a
 * structured user message of the form:
 *
 *   <tool>:<question_id>=<value>; <question_id>=<value>; ...
 *
 * which the parent's onSubmit handler can pass back as the next
 * Felix turn so the agent's input has the same shape regardless
 * of which question the user answered.
 *
 * Empirical anchors (see backend felix_evpi_clarifier.py):
 *   - David 2026-05-02 21:19 — blank-skeleton stories: clarifier
 *     surfaces description-vs-triplet OR-choice + priority +
 *     persona linkage.
 *   - Bob 2026-04-26 — autonomous-action: clarifier asks
 *     "Did you want me to run X right now?".
 */
import { useState } from "react";

interface ClarifierOption {
  label: string;
  value: string;
}

interface ClarifierQuestion {
  question_id: string;
  text: string;
  type: "multiple_choice" | "free_text";
  options: ClarifierOption[];
  hint?: string;
  g_domain?: string;
  evpi?: number;
}

export interface ClarifierPayload {
  question_for_user: string;
  questions: ClarifierQuestion[];
  refused_tool: string;
  refused_error_code: string;
  g_domain: string;
}

interface Props {
  payload: ClarifierPayload;
  onSubmit: (composedMessage: string, raw: Record<string, string>) => void;
  disabled?: boolean;
}

export function FelixChatClarifier({ payload, onSubmit, disabled }: Props) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const allAnswered = payload.questions.every(
    (q) => (answers[q.question_id] ?? "").trim().length > 0,
  );

  /** Human-readable label for an answer, falling back to the raw value. */
  function labelFor(q: (typeof payload.questions)[number], value: string): string {
    const opt = q.options?.find((o) => o.value === value);
    return opt?.label ?? value;
  }

  function handleSubmit() {
    if (!allAnswered || submitted) return;
    setSubmitted(true);
    // The composed string becomes the USER'S MESSAGE in the transcript, so it
    // has to read like something a person said. It used to be built from the
    // internal ids — `${question_id}=${value}` — which put lines like
    //
    //     create_task: loop_break_q_0=different_approach
    //
    // into Bob's conversation as his own words (2026-07-28, Freelance Finder).
    // Felix re-reads that on the next turn, and provenance surfaces it as the
    // user's stated intent.
    //
    // The machine-readable form is not lost: `answers` is already passed to
    // onSubmit as the second argument for exactly that purpose.
    const composed = payload.questions
      .map((q) => {
        const answer = labelFor(q, answers[q.question_id] ?? "");
        return q.text ? `${q.text} ${answer}` : answer;
      })
      .join(" ");
    onSubmit(composed, answers);
  }

  return (
    <div
      // KI-0230 belt-and-suspenders: see FelixChatPlan for the
      // architectural rationale — the parent column now constrains
      // descendants, but each component carries its own min-w-0 +
      // max-w-full so it stays safe wherever it's mounted.
      className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 min-w-0 max-w-full dark:border-amber-700/50 dark:bg-amber-900/20"
      data-testid="felix-clarifier"
      data-refused-tool={payload.refused_tool}
      data-g-domain={payload.g_domain}
    >
      <p className="mb-3 text-sm font-medium text-amber-900 dark:text-amber-100">
        {payload.question_for_user}
      </p>
      <div className="space-y-3">
        {payload.questions.map((q) => (
          <div
            key={q.question_id}
            data-testid={`clarifier-q-${q.question_id}`}
            data-evpi={q.evpi ?? ""}
          >
            <p className="mb-1 text-xs font-medium text-amber-900 dark:text-amber-200">
              {q.text}
            </p>
            {q.type === "multiple_choice" ? (
              <div className="flex flex-wrap gap-2">
                {q.options.map((opt) => {
                  const selected = answers[q.question_id] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        setAnswers((prev) => ({
                          ...prev,
                          [q.question_id]: opt.value,
                        }))
                      }
                      disabled={disabled || submitted}
                      data-testid={`clarifier-opt-${q.question_id}-${opt.value}`}
                      className={
                        "rounded-full border px-3 py-1 text-xs transition-colors " +
                        (selected
                          ? "border-amber-600 bg-amber-200 text-amber-950 dark:border-amber-500 dark:bg-amber-700 dark:text-amber-50"
                          : "border-amber-300 bg-white text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-slate-900 dark:text-amber-200 dark:hover:bg-amber-900/40")
                      }
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <input
                type="text"
                value={answers[q.question_id] ?? ""}
                placeholder={q.hint ?? ""}
                onChange={(e) =>
                  setAnswers((prev) => ({
                    ...prev,
                    [q.question_id]: e.target.value,
                  }))
                }
                disabled={disabled || submitted}
                data-testid={`clarifier-input-${q.question_id}`}
                className="w-full rounded border border-amber-300 bg-white px-2 py-1 text-sm text-amber-900 dark:border-amber-700 dark:bg-slate-900 dark:text-amber-100"
              />
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!allAnswered || disabled || submitted}
          data-testid="clarifier-submit"
          className="rounded-md bg-amber-700 px-3 py-1 text-xs font-medium text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitted ? "Sent" : "Submit"}
        </button>
      </div>
    </div>
  );
}
